import React, { createContext, FC, ReactNode, useEffect, useState, useMemo } from 'react';
import { io, Socket } from 'socket.io-client';
import { backendUrl } from '../custom/constants/urls';
import { useAppSelector } from '../custom/redux/reduxHooks';

export interface CashierScanRequest {
	type: 'deposit' | 'withdrawal';
	amount: number;
	code: number;
	requestId: string;
	expireAt: string;
	cashierPointId: string;
	customer: {
		id: string;
		firstname: string;
		middlename?: string;
		lastname: string;
		fullname: string;
		mobileNumber: string;
	};
}

export interface ISocketContextProps {
	socket: Socket | null;
	scanRequest: CashierScanRequest | null;
	clearScanRequest: () => void;
}

const SocketContext = createContext<ISocketContextProps>({} as ISocketContextProps);

interface ISocketContextProviderProps {
	children: ReactNode;
}

export const SocketContextProvider: FC<ISocketContextProviderProps> = ({ children }) => {
	const [socket, setSocket] = useState<Socket | null>(null);
	const [scanRequest, setScanRequest] = useState<CashierScanRequest | null>(null);
	const { user } = useAppSelector((state) => state.auth);

	useEffect(() => {
		if (!user?.token || !user?.id) {
			console.log('Socket connection skipped: No user token or ID');
			return;
		}

		const socketUrl = backendUrl.replace('/api/v2', '');
		console.log('Attempting socket connection to:', socketUrl);

		const newSocket = io(socketUrl, {
			auth: {
				token: user.token,
			},
			transports: ['websocket', 'polling'],
		});

		newSocket.on('connect', () => {
			console.log('✅ Socket connected successfully:', newSocket.id);
			console.log('📡 Joining admin room');
			newSocket.emit('join_admin_room');
			
			setTimeout(() => {
				console.log('🔍 Socket rooms after join attempt:', newSocket.id);
			}, 1000);
		});

		newSocket.on('connect_error', (err) => {
			console.error('❌ Socket connection error:', err.message);
			console.error('Full error:', err);
		});

		newSocket.on('disconnect', (reason) => {
			console.warn('🔌 Socket disconnected. Reason:', reason);
		});

		newSocket.on('error', (err) => {
			console.error('⚠️ Socket error:', err);
		});

		newSocket.on('admin_room_joined', (data) => {
			console.log('✅ Successfully joined admin room:', data);
		});

		newSocket.on('cashier_deposit_withdraw_request', (data: CashierScanRequest) => {
			console.log('🔔 Received cashier scan request:', data);
			console.log('📋 Setting scan request state...');
			setScanRequest(data);
			console.log('✅ Scan request state updated');
		});

		setSocket(newSocket);

		return () => {
			console.log('🧹 Cleaning up socket connection');
			newSocket.off('connect');
			newSocket.off('connect_error');
			newSocket.off('disconnect');
			newSocket.off('error');
			newSocket.off('admin_room_joined');
			newSocket.off('cashier_deposit_withdraw_request');
			newSocket.disconnect();
		};
	}, [user?.token, user?.id]);

	const clearScanRequest = () => {
		setScanRequest(null);
	};

	const value = useMemo(
		() => ({
			socket,
			scanRequest,
			clearScanRequest,
		}),
		[socket, scanRequest],
	);

	return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
};

export default SocketContext;
